import { useIsNewTicketFlowEnabled } from 'app/services/feature-api';

/**
 * Feature gate for the new ticket dashboard & admin view surfaces.
 *
 * The additive new-flow inputs that only make sense under the new ticket shape —
 * currently the add-customer "why important" impact fields — are gated on this
 * flag via `useIsNewTicketViewEnabled`, so they light up together with the new
 * ticket-creation flows behind the SAME runtime flag (`enableNewTicketFlow`).
 * Redesigned surfaces that render existing data for every ticket (the admin
 * per-customer cards and multi-customer summaries) are not gated: they replace
 * prior UI and must keep rendering when the flag is off, so backward
 * compatibility—not this flag—governs them.
 *
 * This module is a thin wrapper over `useIsNewTicketFlowEnabled` that adds a
 * query-string override for UAT/local verification only.
 *
 * The override is hard-gated to non-production builds: CRA compiles
 * `process.env.NODE_ENV` to the string literal `'production'` in a prod build,
 * so `isNonProdBuild()` is statically false there and the override can never
 * bypass the flag in production.
 */

/** Query-string key that overrides the view gate in non-prod builds only. */
export const VIEW_OVERRIDE_KEY = 'newTicketView';

const isNonProdBuild = (): boolean => process.env.NODE_ENV !== 'production';

/**
 * Parses the view override from a URL query string.
 * Returns `true`/`false` for `?newTicketView=on|true|1` /
 * `?newTicketView=off|false|0`, or `undefined` when there is no (recognised)
 * override or the build is production. Pure — accepts the search string so it
 * can be unit-tested without a browser.
 */
export const resolveViewFlagOverride = (search: string | undefined): boolean | undefined => {
    if (!isNonProdBuild() || !search) {
        return undefined;
    }

    let raw: string | null = null;
    try {
        raw = new URLSearchParams(search).get(VIEW_OVERRIDE_KEY);
    } catch {
        return undefined;
    }
    if (raw === null) {
        return undefined;
    }

    const value = raw.trim().toLowerCase();
    if (value === 'on' || value === 'true' || value === '1') {
        return true;
    }
    if (value === 'off' || value === 'false' || value === '0') {
        return false;
    }
    return undefined;
};

/**
 * Resolves whether the new ticket view surfaces are enabled: the non-prod query
 * override wins when present, otherwise the shared runtime feature flag decides.
 * Defaults to `false` (dark) while the flag is loading or on error.
 */
export const useIsNewTicketViewEnabled = (): boolean => {
    const flagEnabled = useIsNewTicketFlowEnabled();
    const search = typeof window !== 'undefined' ? window.location.search : undefined;
    const override = resolveViewFlagOverride(search);
    return override ?? flagEnabled;
};
